import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { toast } from 'react-toastify';

interface SenderAddress {
    id: number;
    name: string;
    postal_code: string;
    address: string;
    phone_number: string;
}

const AdminSenderAddresses: React.FC = () => {
    const { t } = useTranslation();
    const [addresses, setAddresses] = useState<SenderAddress[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState<Omit<SenderAddress, 'id'>>({
        name: '',
        postal_code: '',
        address: '',
        phone_number: '',
    });

    const fetchAddresses = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/sender-addresses');
            setAddresses(res.data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch addresses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.patch(`/admin/sender-addresses/${editingId}`, formData);
                toast.success(t('admin.edit_success') || 'Updated');
            } else {
                await api.post('/admin/sender-addresses', formData);
                toast.success(t('admin.add_success') || 'Added');
            }
            setEditingId(null);
            setFormData({ name: '', postal_code: '', address: '', phone_number: '' });
            fetchAddresses();
        } catch (err) {
            console.error(err);
            toast.error(t('admin.add_failed') || 'Failed');
        }
    };

    const handleEdit = (sa: SenderAddress) => {
        setEditingId(sa.id);
        setFormData({
            name: sa.name,
            postal_code: sa.postal_code,
            address: sa.address,
            phone_number: sa.phone_number,
        });
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('admin.address_delete_confirm') || 'Delete?')) return;
        try {
            await api.delete(`/admin/sender-addresses/${id}`);
            toast.success(t('admin.delete_success') || 'Deleted');
            fetchAddresses();
        } catch (err) {
            console.error(err);
            toast.error('Delete failed');
        }
    };

    return (
        <div className="p-4 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">{t('admin.address_management')}</h2>

            <div className="bg-white p-6 rounded-xl shadow-sm border mb-8">
                <h3 className="text-lg font-semibold mb-4">
                    {editingId ? t('admin.edit_button') : t('admin.add_new_address')}
                </h3>
                <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.address_name')}</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full border rounded-lg p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.address_postal_code')}</label>
                        <input
                            type="text"
                            required
                            value={formData.postal_code}
                            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                            className="w-full border rounded-lg p-2"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.address_location')}</label>
                        <input
                            type="text"
                            required
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            className="w-full border rounded-lg p-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.address_phone')}</label>
                        <input
                            type="text"
                            required
                            value={formData.phone_number}
                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            className="w-full border rounded-lg p-2"
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                        {editingId && (
                            <button
                                type="button"
                                onClick={() => {
                                    setEditingId(null);
                                    setFormData({ name: '', postal_code: '', address: '', phone_number: '' });
                                }}
                                className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                            >
                                {t('admin.cancel_button')}
                            </button>
                        )}
                        <button
                            type="submit"
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            {t('admin.saving') ? (editingId ? t('admin.save') : t('admin.add_button')) : '...'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                <div className="md:hidden divide-y divide-gray-200">
                    {loading ? (
                        <div className="px-4 py-4 text-center text-gray-500">{t('processing')}</div>
                    ) : addresses.length === 0 ? (
                        <div className="px-4 py-4 text-center text-gray-500">No addresses found</div>
                    ) : (
                        addresses.map((sa) => (
                            <div key={sa.id} className="p-4">
                                {/* 発送元名 */}
                                <div className="text-sm font-semibold text-gray-900 break-words min-w-0">
                                    {sa.name}
                                </div>
                                {/* 各項目（ラベル：値） */}
                                <div className="mt-2 space-y-1 text-xs">
                                    <div className="break-all">
                                        <span className="text-gray-400">{t('admin.address_phone')}：</span>
                                        <span className="text-gray-700">{sa.phone_number}</span>
                                    </div>
                                    <div className="break-all">
                                        <span className="text-gray-400">{t('admin.address_postal_code')}：</span>
                                        <span className="text-gray-700">〒{sa.postal_code}</span>
                                    </div>
                                    <div className="break-words">
                                        <span className="text-gray-400">{t('admin.address_location')}：</span>
                                        <span className="text-gray-700">{sa.address}</span>
                                    </div>
                                </div>
                                {/* 操作（編集/削除） */}
                                <div className="mt-3 flex items-center gap-3 flex-wrap text-sm font-medium">
                                    <button
                                        onClick={() => handleEdit(sa)}
                                        className="shrink-0 text-indigo-600 hover:text-indigo-900"
                                    >
                                        {t('admin.edit_button')}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(sa.id)}
                                        className="shrink-0 text-red-600 hover:text-red-900"
                                    >
                                        {t('admin.delete_button')}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* ▼ デスクトップ: テーブル表示 */}
                <table className="hidden md:table min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('admin.address_name')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('admin.address_location')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                                {t('admin.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                                    {t('processing')}
                                </td>
                            </tr>
                        ) : addresses.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                                    No addresses found
                                </td>
                            </tr>
                        ) : (
                            addresses.map((sa) => (
                                <tr key={sa.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">{sa.name}</div>
                                        <div className="text-xs text-gray-500">{sa.phone_number}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">〒{sa.postal_code}</div>
                                        <div className="text-sm text-gray-500">{sa.address}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleEdit(sa)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                                        >
                                            {t('admin.edit_button')}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(sa.id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            {t('admin.delete_button')}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminSenderAddresses;
